"use client"

import * as React from "react"
import { Area, AreaChart, CartesianGrid, Line, ReferenceLine, XAxis, YAxis } from "recharts"
import {
  ChartDataTable,
  ChartFigure,
  ChartCard,
  ChartLeoPlotInsightOverlay,
  type ChartLeoInsight,
} from "@/components/charts-core"
import {
  ChartContainer,
  ChartTooltip,
  chartTooltipKeyboardSyncProps,
  ChartTooltipContent,
  type ChartConfig,
} from "@/components/ui/chart"
import { CHART_AXIS_TICK } from "@/lib/chart-typography"
import { buildTermResponseChartData } from "@/lib/mock/evaluation-dashboard-kpi"
import { shortTermChartLabel, type EvaluationTerm } from "@/lib/mock/evaluation-dashboard"

const RESPONSE_TARGET = 70
const RESPONSE_ABOVE_FILL_ID = "eval-response-rate-above"
const RESPONSE_BELOW_FILL_ID = "eval-response-rate-below"
const RESPONSE_ABOVE_STROKE = "var(--color-chart-2)"
const RESPONSE_BELOW_STROKE = "var(--destructive)"
const SPRING_2025_CHART_LABEL = "Spring '25"

const RESPONSE_RATE_LEO_INSIGHT: ChartLeoInsight = {
  headline: "Spring 2025 response rate fell below target",
  explanation:
    "Spring 2025 closed at 30%, well under the 70% program target. Fall 2024 also finished at 40%. Both terms need follow up before the next collection window.",
  kind: "dip",
  delta: { value: "30%", label: "Spring 2025" },
  bullets: [
    "Fall 2024 closed at 40%, also below the 70% target.",
    "Summer 2025 recovered to 78% before the most recent closed term.",
  ],
  anchor: { xValue: SPRING_2025_CHART_LABEL, yDataKeys: ["rate"] },
  askLeoPrompt:
    "Why did Spring 2025 and Fall 2024 response rates fall below our 70% target, and which courses drove the gap?",
}

type ResponseChartRow = ReturnType<typeof buildTermResponseChartData>[number]

type ThresholdChartPoint = {
  name: string
  x: number
  rate: number
  pointId: string
  isCrossing?: boolean
  [segmentKey: string]: string | number | boolean | null | undefined
}

type ThresholdSegment = {
  id: string
  tone: "above" | "below"
  pointIds: Set<string>
}

const RESPONSE_CFG: ChartConfig = {
  rate: { label: "Response rate", color: RESPONSE_ABOVE_STROKE },
}

interface TermResponseTrendChartProps {
  /** Last closed term — emphasized dot on the trend. */
  focusTerm?: EvaluationTerm
}

function buildThresholdChartData(data: ResponseChartRow[], target: number) {
  if (data.length === 0) {
    return {
      chartData: [] as ThresholdChartPoint[],
      segments: [] as ThresholdSegment[],
      termTicks: [] as number[],
      termLabels: [] as string[],
    }
  }

  const termTicks = data.map((_, index) => index)
  const termLabels = data.map(row => row.name)
  const expanded: ThresholdChartPoint[] = [
    {
      name: data[0].name,
      x: 0,
      rate: data[0].rate,
      pointId: "t0",
    },
  ]
  const segments: ThresholdSegment[] = []
  let currentTone: ThresholdSegment["tone"] = data[0].rate >= target ? "above" : "below"
  let segmentPointIds = new Set(["t0"])
  let segmentIndex = 0

  for (let i = 1; i < data.length; i++) {
    const prev = data[i - 1]
    const curr = data[i]
    const prevAbove = prev.rate >= target
    const currAbove = curr.rate >= target

    if (prevAbove !== currAbove) {
      const crossId = `c${i}`
      expanded.push({
        name: "",
        x: i - 0.5,
        rate: target,
        pointId: crossId,
        isCrossing: true,
      })
      segmentPointIds.add(crossId)
      segments.push({
        id: `rateSeg${segmentIndex++}`,
        tone: currentTone,
        pointIds: segmentPointIds,
      })
      currentTone = currAbove ? "above" : "below"
      segmentPointIds = new Set([crossId, `t${i}`])
    } else {
      segmentPointIds.add(`t${i}`)
    }

    expanded.push({
      name: curr.name,
      x: i,
      rate: curr.rate,
      pointId: `t${i}`,
    })
  }

  segments.push({
    id: `rateSeg${segmentIndex}`,
    tone: currentTone,
    pointIds: segmentPointIds,
  })

  const chartData = expanded.map(point => {
    const row: ThresholdChartPoint = {
      name: point.name,
      x: point.x,
      rate: point.rate,
      pointId: point.pointId,
    }
    if (point.isCrossing) row.isCrossing = true
    for (const segment of segments) {
      row[segment.id] = segment.pointIds.has(point.pointId) ? point.rate : null
    }
    return row
  })

  return { chartData, segments, termTicks, termLabels }
}

function responseRateSummary(data: ResponseChartRow[]): string {
  return data.length > 0
    ? data.map(row => `${row.name} ${row.rate}%`).join(", ")
    : "No closed terms yet."
}

function responseRateDotColor(rate: number): string {
  return rate >= RESPONSE_TARGET ? RESPONSE_ABOVE_STROKE : RESPONSE_BELOW_STROKE
}

function segmentStroke(tone: ThresholdSegment["tone"]): string {
  return tone === "above" ? RESPONSE_ABOVE_STROKE : RESPONSE_BELOW_STROKE
}

function segmentFill(tone: ThresholdSegment["tone"]): string {
  return tone === "above"
    ? `url(#${RESPONSE_ABOVE_FILL_ID})`
    : `url(#${RESPONSE_BELOW_FILL_ID})`
}

function ResponseRateAreaChart({
  data,
  focusLabel,
}: {
  data: ResponseChartRow[]
  focusLabel: string | null
}) {
  const { chartData, segments, termTicks, termLabels } = React.useMemo(
    () => buildThresholdChartData(data, RESPONSE_TARGET),
    [data],
  )
  const leoPlotData = React.useMemo(
    () => chartData.filter(point => !point.isCrossing),
    [chartData],
  )
  const summary = responseRateSummary(data)

  return (
    <ChartFigure
      label="Response rate by term"
      summary={summary}
      dataLength={chartData.length}
      leoInsight={RESPONSE_RATE_LEO_INSIGHT}
    >
      {activeIndex => (
        <>
          <div className="relative w-full min-h-[200px]">
            <ChartContainer config={RESPONSE_CFG} className="h-[200px] w-full">
              <AreaChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id={RESPONSE_BELOW_FILL_ID} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={RESPONSE_BELOW_STROKE} stopOpacity={0.35} />
                    <stop offset="95%" stopColor={RESPONSE_BELOW_STROKE} stopOpacity={0.02} />
                  </linearGradient>
                  <linearGradient id={RESPONSE_ABOVE_FILL_ID} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={RESPONSE_ABOVE_STROKE} stopOpacity={0.35} />
                    <stop offset="95%" stopColor={RESPONSE_ABOVE_STROKE} stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid vertical={false} strokeDasharray="3 3" />
                <XAxis
                  dataKey="x"
                  type="number"
                  domain={[0, Math.max(data.length - 1, 0)]}
                  ticks={termTicks}
                  tick={CHART_AXIS_TICK}
                  tickFormatter={value => termLabels[Number(value)] ?? ""}
                  allowDecimals={false}
                />
                <YAxis domain={[0, 100]} tick={CHART_AXIS_TICK} width={36} />
                <ReferenceLine
                  y={RESPONSE_TARGET}
                  stroke="var(--color-chart-4)"
                  strokeDasharray="4 4"
                  label={{
                    value: `Target ${RESPONSE_TARGET}%`,
                    position: "insideTopRight",
                    fill: "var(--muted-foreground)",
                    fontSize: 11,
                  }}
                />
                <ChartTooltip
                  key={chartTooltipKeyboardSyncProps(activeIndex).key}
                  {...chartTooltipKeyboardSyncProps(activeIndex).props}
                  content={({ active, payload }) => {
                    const row = payload?.find(item => item.dataKey === "rate")
                      ?.payload as ThresholdChartPoint | undefined
                    if (!row || row.isCrossing) return null
                    const swatchColor = responseRateDotColor(row.rate)
                    const ratePayload = payload?.filter(item => item.dataKey === "rate") ?? []
                    return (
                      <ChartTooltipContent
                        active={active}
                        payload={ratePayload.map(item => ({
                          ...item,
                          color: swatchColor,
                        }))}
                        label={row.name}
                        hideLabel
                        indicator="dot"
                        color={swatchColor}
                      />
                    )
                  }}
                />
                {segments.map(segment => (
                  <Area
                    key={segment.id}
                    type="monotone"
                    dataKey={segment.id}
                    connectNulls={false}
                    stroke={segmentStroke(segment.tone)}
                    fill={segmentFill(segment.tone)}
                    strokeWidth={2}
                    isAnimationActive={false}
                    legendType="none"
                    tooltipType="none"
                    dot={false}
                    activeDot={false}
                  />
                ))}
                <Line
                  type="monotone"
                  dataKey="rate"
                  stroke="none"
                  fill="none"
                  isAnimationActive={false}
                  legendType="none"
                  dot={props => {
                    const { cx, cy, index, key, payload } = props
                    if (cx == null || cy == null) return null
                    const row = payload as ThresholdChartPoint
                    if (row.isCrossing) return null
                    const isFocus = focusLabel != null && row.name === focusLabel
                    const isActive = index === activeIndex
                    const emphasized = isActive || isFocus
                    const fill = responseRateDotColor(row.rate)
                    return (
                      <circle
                        key={key}
                        cx={cx}
                        cy={cy}
                        r={emphasized ? 5 : 3}
                        fill={fill}
                        stroke={emphasized ? "var(--ring)" : undefined}
                        strokeWidth={emphasized ? 2 : 0}
                      />
                    )
                  }}
                  activeDot={{ r: 5, stroke: "var(--ring)", strokeWidth: 2 }}
                />
              </AreaChart>
            </ChartContainer>
            <ChartLeoPlotInsightOverlay
              data={
                leoPlotData as ReadonlyArray<
                  Record<string, string | number | null | undefined>
                >
              }
              xDataKey="x"
              chartFamily="line"
            />
          </div>
          <ChartDataTable
            caption="Response rate by term"
            headers={["Term", "Rate"]}
            rows={data.map(row => [row.name, `${row.rate}%`])}
          />
        </>
      )}
    </ChartFigure>
  )
}

/** Response rate across closed and live terms. */
export function TermResponseTrendChart({ focusTerm }: TermResponseTrendChartProps) {
  const responseData = React.useMemo(() => buildTermResponseChartData(), [])
  const focusLabel = focusTerm ? shortTermChartLabel(focusTerm.termName) : null

  if (responseData.length === 0) return null

  return (
    <ChartCard title="Response rate by term" description="Closed terms and live window">
      <ResponseRateAreaChart data={responseData} focusLabel={focusLabel} />
    </ChartCard>
  )
}

/** @deprecated Use TermResponseTrendChart */
export const ClosedTermResponseTrend = TermResponseTrendChart
